import { AwsUi } from '../../../core/runtime/src';
import BaseInput from '../../input/src/base-input';
import { KeyCode } from '../../../core/utils/src/dom';
import DateInputMaskController from './controllers/date-input-mask-controller';
import Dropdown from './dropdown';
import Calendar from './calendar';
import { daysInMonth, displayToIso, formatDate, isoToDisplay, memoizedDate, parseDate } from './utils/date';
function daysMax(value) {
    // force to first day in month, as new Date('2018-02-30') -> March 2nd 2018
    var baseDate = displayToIso(value).substring(0, 7);
    return daysInMonth(parseDate(baseDate));
}
var format = {
    separator: '/',
    inputSeparators: ['-', '.', ' '],
    segments: [
        //prettier-ignore
        { min: 0, max: 9999, default: 2000, length: 4 },
        { min: 1, max: 12, length: 2 },
        { min: 1, max: daysMax, length: 2 }
    ]
};
export var DatePicker = AwsUi.__addComponent('awsui-date-picker', {
    releaseStatus: 'stable',
    callbacks: {
        initialized: function () {
            this.__setupMaskController();
            this.__updateDisplayedDate();
            this.__onChangeMonth = this.__onChangeMonth.bind(this);
            this.__onFocusDate = this.__onFocusDate.bind(this);
            this.__onSelectDate = this.__onSelectDate.bind(this);
            if (!this.controlId) {
                this.controlId = this.__maskController.generatedId;
            }
        },
        attached: function () {
            this.__maskController.setNode(this.node);
            this.__maskController.addListeners();
        },
        detached: function () {
            this.__maskController.removeListeners();
        }
    },
    properties: {
        // The current input value. This should be provided in YYYY-MM-DD format.
        value: {
            type: 'string',
            defaultValue: '',
            onChange: function (oldValue, newValue) {
                if (newValue === '') {
                    return;
                }
                var updated = newValue;
                // apply reformat to check for month overflow
                updated = displayToIso(this.__maskController.reformat(format, isoToDisplay(updated)));
                // validate the value
                updated = displayToIso(this.__maskController.maskedValueAttributeChangeHandler(isoToDisplay(oldValue), isoToDisplay(updated)));
                if (updated !== newValue) {
                    this.value = updated;
                }
                this.__updateDisplayedDate();
            }
        },
        // Placeholder text rendered when the value is an empty string.
        placeholder: {
            type: 'string'
        },
        // A function that defines whether a particular date should be enabled in the calendar or not.
        // Note that disabling a date in the calendar still allows users to enter this date via keyboard.
        // We therefore recommend that you also validate these constraints client- and server-side, in the
        // same way as for other form elements.
        isDateEnabled: {
            type: 'function',
            functionType: 'DatePicker.IsDateEnabledFunction'
        },
        // The locale to be used for rendering month names and defining the starting date of the week.
        // If not provided, it will be determined from the page and browser locales.
        // Supported values and formats are as-per the
        // [JavaScript Intl API specification](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#Locale_identification_and_negotiation).
        locale: {
            type: 'string'
        },
        // Starting day of the week. [0-6] maps to [Sunday-Saturday].
        // By default the starting day of the week is defined by the locale, but you can override it using this property.
        startOfWeek: {
            type: 'integer',
            nullable: true
        },
        // Used as part of the aria label for today's date in the calendar.
        todayLabel: {
            type: 'string'
        },
        // An aria label for the 'next month' button.
        nextMonthLabel: {
            type: 'string'
        },
        // An aria label for the 'previous month' button.
        previousMonthLabel: {
            type: 'string'
        },
        // The name of the control used in HTML forms.
        name: {
            type: 'string'
        },
        // Specifies that the input should be disabled, preventing the user from
        // modifying the value and preventing the value from being included in a
        // form submission. A disabled input cannot receive focus.
        //
        // **AngularJS:** Use `data-disabled` instead.
        disabled: {
            type: 'boolean'
        },
        // Specifies that the input should be readonly, preventing the user from
        // modifying the value but including it in a form submission. A readonly input can receive focus.
        //
        // Do not use readonly inputs outside of a form.
        readonly: {
            type: 'boolean'
        },
        // Overrides invalidation state. Usually invalid state comes from wrapping `awsui-form-field` component, however
        // sometimes you need to override its state when you have more than one input within single form-field.
        invalid: {
            type: 'boolean',
            nullable: true
        },
        // Allows you to indicate that the control is to be focused as
        // soon as the `load` event triggers, allowing the user to just start
        // typing without having to manually focus the input. Don't use this option
        // in pages that allow for the field to be scrolled out of the viewport.
        autofocus: {
            type: 'boolean'
        },
        // Id of the internal input. Useful for relating a label element's
        // "for" attribute to this control. It defaults to an automatically generated id.
        controlId: {
            type: 'string',
            announceValue: true,
            onChange: function () {
                if (!this.controlId) {
                    this.controlId = this.__maskController.generatedId;
                }
            }
        },
        // Adds an aria label on the native input.
        label: {
            type: 'string'
        },
        // Adds aria-labelledby on the native input. Use this only with form fields that
        // contain multiple controls under the same label.
        // 1. Define a custom id inside the label
        // 2. Refer to that label from every single control under that label using this property
        ariaLabelledby: {
            type: 'string'
        },
        // Adds aria-describedby on the native input. Use this only with form fields that
        // contain multiple controls under the same label.
        // 1. Define custom ids inside the description, hint and error text
        // 2. Refer to these from every single control under that label using this property
        // 3. Refer to any other hint/description text that you provide
        ariaDescribedby: {
            type: 'string',
            nullable: true
        },
        // Adds aria-required on the native input
        ariaRequired: {
            type: 'boolean'
        },
        // A default suggested aria-describedby value that can be pushed down from a FormField
        __suggestedAriaDescribedby: {
            type: 'string'
        },
        __confirmedValue: {
            type: 'string',
            defaultValue: ''
        },
        __dropdownIsOpen: {
            type: 'boolean'
        },
        __calendarHasFocus: {
            type: 'boolean'
        },
        __displayedDate: {
            type: 'string'
        },
        __focusedDate: {
            type: 'string'
        }
    },
    functions: {
        // Sets input focus onto the UI control.
        focus: function () {
            this.node.querySelector('input').focus();
        },
        __setupMaskController: function () {
            var _this = this;
            this.__maskController = new DateInputMaskController({
                getValue: function () { return isoToDisplay(_this.value); },
                isReadOnly: function () { return _this.readonly; },
                isDisabled: function () { return _this.disabled; },
                notifyInput: function (value) {
                    _this.value = displayToIso(value);
                    _this.__fireEvent('input', { value: _this.value });
                    _this.__openDropdown();
                },
                notifyChange: function (value) {
                    _this.value = displayToIso(value);
                    _this.__fireEvent('change', { value: _this.value });
                    _this.__maskController.autocompleteNextChange = true;
                },
                overrideValue: function (value) {
                    _this.value = displayToIso(value);
                },
                focus: function () { return _this.focus(); },
                notifyFocus: function () { return _this.__fireEvent('focus'); },
                notifyBlur: function () { return _this.__fireEvent('blur'); },
                notifyKeyEvent: function (type, detail, extra) {
                    var event = extra.nativeEvent;
                    if (event.keyCode === KeyCode.enter && event.target.nodeName === 'INPUT') {
                        _this.__closeDropdown();
                    }
                    if (event.keyCode === KeyCode.down) {
                        _this.__maskController.autocompleteNextChange = false;
                    }
                }
            }, this.node);
            this.__maskController.setFormat(format);
        },
        __updateDisplayedDate: function () {
            this.__displayedDate = formatDate(this.value.length >= 4 ? parseDate(this.value) : new Date());
        },
        __onChangeMonth: function (newMonth) {
            this.__displayedDate = formatDate(newMonth);
            this.__focusedDate = null;
        },
        __onSelectDate: function (_a) {
            var date = _a.date;
            // Focus on the input field before changing the this.__dropdownIsOpen flag to
            // false to make sure that calling the focus method doesn't re-open the dropdown
            this.focus();
            this.__closeDropdown();
            this.__maskController.sendChangeIfNeeded(formatDate(date));
        },
        __onFocusDate: function (_a) {
            var date = _a.date;
            this.__focusedDate = date && formatDate(date);
        },
        __openDropdown: function () {
            if (!this.__dropdownIsOpen) {
                this.__updateDisplayedDate();
                this.__dropdownIsOpen = true;
            }
        },
        __closeDropdown: function () {
            if (this.__dropdownIsOpen) {
                this.__dropdownIsOpen = false;
                this.__blurCalendar();
            }
        },
        __focusCalendar: function () {
            this.__calendarHasFocus = true;
        },
        __blurCalendar: function () {
            this.__calendarHasFocus = false;
            this.__focusedDate = null;
        }
    },
    events: {
        // Fired when input focus is set onto the UI control.
        focus: {
            bubbles: true,
            cancelable: false
        },
        // Fired when input focus is removed from the UI control.
        blur: {
            bubbles: true,
            cancelable: false
        },
        // Mimics the native `change` event on inputs.
        // The event `detail` contains the current value of the field.
        change: {
            bubbles: true,
            cancelable: false,
            detailType: 'DatePicker.ChangeDetail'
        },
        // Mimics the native `input` event on inputs.
        // The event `detail` contains the current value of the field.
        input: {
            bubbles: true,
            cancelable: false,
            detailType: 'DatePicker.ChangeDetail'
        }
    },
    render: function (m, h) {
        var _this = this;
        var Input = (AwsUi.__createElement(BaseInput, { value: isoToDisplay(this.value), rightIcon: "calendar", rightIconVariant: "subtle", autocomplete: false, disableBrowserAutocorrect: true, disabled: this.disabled, readonly: this.readonly, "aria-invalid": this.invalid, "aria-required": this.ariaRequired, additionalAttrs: {
                name: this.name,
                id: this.controlId || null,
                placeholder: this.placeholder,
                autofocus: this.autofocus,
                'aria-describedby': this.ariaDescribedby || this.__suggestedAriaDescribedby || undefined,
                'aria-label': this.label,
                'aria-labelledby': this.ariaLabelledby
            } }));
        if (this.disabled || this.readonly) {
            return Input;
        }
        return (AwsUi.__createElement(Dropdown, { isOpen: this.__dropdownIsOpen, onOpen: function () { return _this.__openDropdown(); }, onClose: function () { return _this.__closeDropdown(); }, focusContent: function () { return _this.__focusCalendar(); }, input: Input, content: 
            // Our current TSX typings don't understand mithril components.
            // Something like https://github.com/andraaspar/mithril-tsx-component/blob/master/index.ts
            // could be a solution, but that requires a Class-based mithril component, which our Runtime
            // also doesn't currently handle.
            // @ts-ignore
            AwsUi.__createElement(Calendar, { locale: this.locale, startOfWeek: this.startOfWeek, selectedDate: this.value.length >= 10 ? memoizedDate('value', this.value) : null, focusedDate: memoizedDate('focused', this.__focusedDate), displayedDate: memoizedDate('displayed', this.__displayedDate), isDateEnabled: this.isDateEnabled, todayLabel: this.todayLabel, nextMonthLabel: this.nextMonthLabel, previousMonthLabel: this.previousMonthLabel, hasFocus: this.__calendarHasFocus, onChangeMonth: this.__onChangeMonth, onSelectDate: this.__onSelectDate, onFocusDate: this.__onFocusDate, onMouseDown: function () { return (_this.__maskController.autocompleteNextChange = false); } }) }));
    }
});
//# sourceMappingURL=date-picker.js.map